import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../api";
import { Alert, Button } from "../../components/ui";

export function useResource<T>(path: string | null) {
  const api = useApi();
  const { t } = useTranslation("settings");
  const translate = useRef(t);
  translate.current = t;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [error, setError] = useState("");
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const request = ++generation.current;
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const value = await api.get(path);
      if (request === generation.current) setData(value);
    } catch {
      if (request === generation.current)
        setError(translate.current("common.loadFailed"));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [api, path]);
  useEffect(() => {
    setData(null);
    void reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);
  return { data, loading, error, reload };
}

export function useAction() {
  const { t } = useTranslation("settings");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const pending = useRef(false);
  const run = async (action: () => Promise<void>, message = "") => {
    if (pending.current) return false;
    pending.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      await action();
      setSuccess(message);
      return true;
    } catch (e: unknown) {
      const problem = e as { status?: number; code?: string };
      setError(
        problem.status === 409
          ? t("common.conflict")
          : problem.status === 504
            ? t("common.timeout")
            : problem.status === 403
              ? t("common.denied")
              : t("common.actionFailed"),
      );
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return { busy, error, success, setError, setSuccess, run };
}

export function Feedback({
  error,
  success,
}: {
  error?: string;
  success?: string;
}) {
  return (
    <>
      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
    </>
  );
}
export function LoadError({
  error,
  reload,
}: {
  error: string;
  reload: () => Promise<void>;
}) {
  const { t } = useTranslation("settings");
  return error ? (
    <Alert variant="error">
      <div className="settings-inline">
        <span>{error}</span>
        <Button variant="outline" size="sm" onClick={() => void reload()}>
          {t("common.retry")}
        </Button>
      </div>
    </Alert>
  ) : null;
}
export const dateLabel = (value: string | undefined | null, locale: string) =>
  value
    ? new Intl.DateTimeFormat(locale, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
export { printerIsOnline, type Printer } from "./printing-safety";
